import { useTranslation } from 'react-i18next'

import {
  type PlanTableColumn,
  type PlanTableColumnSpec,
  PlanTable as SharedPlanTable,
} from '~/components/planSubmit/PlanTable'
import type { SlotValue } from '~/pages/GenerationSalesPlanDetail/models/calc'
import type { BreakdownColumn } from '~/pages/GenerationSalesPlanDetail/models/columns'

type Props = {
  /** 時刻ラベル ("00:00" 〜 "23:30") */
  timeLabels: readonly string[]
  /** 発電計画合計（frontend 算出、各コマ）。発電BG別内訳は本画面では列として表示しない
   * （発電所別内訳は別画面 `/gen-plan-detail` の責務、spec スコープ外）。 */
  generationTotal: SlotValue[]
  /** 販売取引先別内訳列 */
  salesColumns: BreakdownColumn[]
  /** 販売計画合計（frontend 算出、各コマ） */
  salesTotal: SlotValue[]
  /** 調達取引先別内訳列 */
  procurementColumns: BreakdownColumn[]
  /** 調達計画合計（frontend 算出、各コマ） */
  procurementTotal: SlotValue[]
  /** 需給バランス（frontend 算出、各コマ）= 発電 + 調達 − 販売（spec AC-006） */
  balances: SlotValue[]
  /** 発電計画合計の前回差分フラグ */
  generationDiffFlags: boolean[]
  /** 前回提出データが存在するか */
  hasPreviousSubmitted: boolean
}

function toPlanTableColumn(col: BreakdownColumn): PlanTableColumn {
  return {
    key: col.key,
    label: col.label,
    counterpartyCode: col.counterpartyCode,
    slots: col.slots,
    diffFlags: col.diffFlags,
    fixedLabel: col.fixedLabel,
  }
}

/**
 * 発電販売計画 48 コマテーブル（read-only）。
 *
 * 列構成（spec AC-002、2 行 thead）:
 *   1 行目: 時刻(rowspan2) | 需給バランス(rowspan2) | 発電計画合計(rowspan2)
 *           | 販売計(rowspan2) | 販売 (N)(colspan=N)
 *           | 調達計(rowspan2) | 調達 (M)(colspan=M)
 *   2 行目: 販売×N の counterparty ヘッダ … | 調達×M の counterparty ヘッダ …
 *
 * 発電BG別内訳は列として表示しない（発電所別内訳は別画面 `/gen-plan-detail` の責務、
 * spec §スコープ外）。発電計画合計は単一列（frontend が generationValues から算出）。
 *
 * セル描画・色定数・罫線モデルは共通 `components/planSubmit/PlanTable`
 * （ProcurementPlanDetail と 1 本化）に委譲し、本ファイルは列の表示順
 * （販売計 → 販売群 → 調達計 → 調達群）と i18n ラベル解決のみを担う。
 */
export const PlanTable = ({
  timeLabels,
  generationTotal,
  salesColumns,
  salesTotal,
  procurementColumns,
  procurementTotal,
  balances,
  generationDiffFlags,
  hasPreviousSubmitted,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const columnSpecs: PlanTableColumnSpec[] = [
    {
      kind: 'total',
      key: 'salesTotal',
      variant: 'sales',
      label: t('page:generationSalesPlanDetail.table.salesTotal'),
      values: salesTotal,
    },
    {
      kind: 'group',
      key: 'sales',
      variant: 'sales',
      groupLabel: t('page:generationSalesPlanDetail.table.salesGroup', {
        count: salesColumns.length,
      }),
      columns: salesColumns.map(toPlanTableColumn),
    },
    {
      kind: 'total',
      key: 'procurementTotal',
      variant: 'procurement',
      label: t('page:generationSalesPlanDetail.table.procurementTotal'),
      values: procurementTotal,
    },
    {
      kind: 'group',
      key: 'procurement',
      variant: 'procurement',
      groupLabel: t('page:generationSalesPlanDetail.table.procurementGroup', {
        count: procurementColumns.length,
      }),
      columns: procurementColumns.map(toPlanTableColumn),
    },
  ]

  return (
    <SharedPlanTable
      balanceHeaderLabel={t(
        'page:generationSalesPlanDetail.table.supplyDemandBalance',
      )}
      balances={balances}
      columnSpecs={columnSpecs}
      dailyTotalLabel={t('page:generationSalesPlanDetail.table.dailyTotal')}
      dailyTotalTooltip={t(
        'page:generationSalesPlanDetail.table.dailyTotalTooltip',
      )}
      emptyLabel={t('page:generationSalesPlanDetail.cell.empty')}
      hasPreviousSubmitted={hasPreviousSubmitted}
      keyColumn={{
        headerLabel: t('page:generationSalesPlanDetail.table.generationTotal'),
        values: generationTotal,
        diffFlags: generationDiffFlags,
      }}
      noCounterpartyCodeLabel={t('page:generationSalesPlanDetail.header.empty')}
      timeHeaderLabel={t('page:generationSalesPlanDetail.table.time')}
      timeLabels={timeLabels}
    />
  )
}
